// =====================================================================
// SecuriteLegal.jsx — M-010 : échéancier des contrôles périodiques
// =====================================================================
// L'onglet 7 de leur fichier commence par : « Priorité absolue : c'est le
// premier document que demande l'inspection du Contrôle du bien-être au
// travail. » Cet écran est donc conçu pour être montré tel quel à un
// inspecteur, et pour se remplir par bouts le reste du temps.
//
// Trois partis pris :
//
//   · « À vérifier » n'est pas « en retard ». Nous ne savons pas si le
//     contrôle sprinkler 2026 a eu lieu ; le dire autrement serait faux, et
//     un tableau qui crie au rouge sur des retards inventés ne sera pas cru
//     sur les vrais.
//
//   · Ce que la tournée du vendredi prouve déjà est affiché sur la ligne,
//     avec la date, la réponse et le nom. C'est le décalage inverse qui a
//     fait abandonner le fichier Excel : il annonçait « à déterminer » pour
//     des vérifications faites trois jours plus tôt.
//
//   · Les points soulevés nommément par Mensura le 01/09/2025 sont marqués.
//     Ce sont ceux sur lesquels on sera réinterrogé en premier.
// =====================================================================

import { useState, useEffect, useCallback, Fragment } from 'react';
import { safetyLegal, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';

const C = {
  line: '#e5e7eb', ink: '#3f4654', mute: '#7b8494', brand: '#714B67',
  ok: '#16a34a', okBg: '#effaf3',
  warn: '#d97706', warnBg: '#fdf5e7',
  crit: '#dc2626', critBg: '#fdf0f0',
  info: '#3730a3', infoBg: '#e0e7ff',
};

const STATUS = {
  A_DETERMINER: { label: 'à déterminer', bg: '#f1f5f9', fg: '#475569',
    help: 'information manquante — c’est ce que l’inspection repère en premier' },
  A_VERIFIER:   { label: 'à vérifier',   bg: C.warnBg, fg: C.warn,
    help: 'l’échéance est passée, mais nous ne savons pas si le contrôle a eu lieu' },
  PLANIFIE:     { label: 'planifié',     bg: C.infoBg, fg: C.info,
    help: 'prochaine échéance connue et pas encore atteinte' },
  FAIT:         { label: 'fait',         bg: C.okBg,   fg: '#065F46',
    help: 'contrôle réalisé et rapport classé' },
  EN_RETARD:    { label: 'en retard',    bg: C.critBg, fg: C.crit,
    help: 'nous savons que le contrôle n’a pas eu lieu' },
  SANS_OBJET:   { label: 'sans objet',   bg: '#f1f5f9', fg: '#94a3b8',
    help: 'installation absente ou sous le seuil soumis à contrôle' },
};

const card = {
  background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12,
  padding: '1rem 1.1rem', boxShadow: '0 1px 3px rgba(60,50,90,.06)',
};
const inp = {
  width: '100%', padding: '.4rem .5rem', border: `1px solid ${C.line}`,
  borderRadius: 6, fontSize: '.85rem', fontFamily: 'inherit', boxSizing: 'border-box',
};
const lbl = { display: 'block', fontSize: '.72rem', fontWeight: 600, color: C.mute,
  textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 3 };

const fr = (d) => (d ? new Date(d).toLocaleDateString('fr-BE',
  { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const iso = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

export default function SecuriteLegal() {
  const { can } = useAuth();
  const canEdit = can('maintenance.plan.all');

  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(null);      // id de la ligne dépliée
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('tous');

  const load = useCallback(async () => {
    try {
      const d = await safetyLegal.list();
      setRows(d.items); setSum(d.summary);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function edit(r) {
    setOpen(r.id === open ? null : r.id);
    setDraft({
      organism: r.organism || '', controlType: r.controlType || '',
      lastControlDate: iso(r.lastControlDate), lastControlNote: r.lastControlNote || '',
      frequencyMonths: r.frequencyMonths ?? '', frequencyNote: r.frequencyNote || '',
      status: r.status, remarks: r.remarks || '', reportPath: r.reportPath || '',
    });
  }

  async function save(id) {
    setSaving(true); setErr(null);
    try {
      const d = await safetyLegal.update(id, draft);
      setRows(d.items); setSum(d.summary); setOpen(null);
    } catch (e) { setErr(errMsg(e)); } finally { setSaving(false); }
  }

  const shown = rows.filter((r) => {
    if (filter === 'mensura') return r.raisedByMensura;
    if (filter === 'manquant') return r.status === 'A_DETERMINER';
    if (filter === 'echu') return r.overdue || r.status === 'A_VERIFIER';
    if (filter === 'couvert') return r.coveredBy && r.coveredBy.date;
    return true;
  });

  if (busy) return <p style={{ color: C.mute }}>Chargement de l’échéancier…</p>;

  return (
    <div>
      {err && (
        <div style={{ ...card, borderColor: '#f3c7c7', background: C.critBg,
          color: C.crit, marginBottom: '.9rem' }}>{err}</div>
      )}

      <div style={{ ...card, marginBottom: '.9rem' }}>
        <h3 style={{ margin: '0 0 .3rem', fontSize: '1rem', color: C.ink }}>
          Échéancier des contrôles périodiques et légaux
        </h3>
        <p style={{ margin: '0 0 .9rem', fontSize: '.86rem', color: C.mute, maxWidth: '68ch' }}>
          C’est le premier document que demande l’inspection du Contrôle du bien-être
          au travail. Chaque ligne se complète au fur et à mesure — un rapport
          retrouvé, un organisme identifié — et ce qui est déjà vérifié chaque
          vendredi s’affiche tout seul sur la ligne concernée.
        </p>

        {sum && (
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {[
              ['tous', `${sum.total} contrôles`, '#f1f5f9', '#475569'],
              ['manquant', `${sum.aDeterminer} information manquante`, '#f1f5f9', '#475569'],
              ['echu', `${sum.aVerifier} à vérifier`, C.warnBg, C.warn],
              ['mensura', `${sum.mensura} soulevés par Mensura`, C.critBg, C.crit],
              ['couvert', `${sum.couverts} prouvés par la tournée`, C.okBg, '#065F46'],
            ].map(([k, txt, bg, fg]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                style={{
                  border: filter === k ? `2px solid ${fg}` : `1px solid ${C.line}`,
                  background: bg, color: fg, borderRadius: 999,
                  padding: '.3rem .75rem', fontSize: '.82rem', fontWeight: 600,
                  cursor: 'pointer',
                }}>{txt}</button>
            ))}
          </div>
        )}

        <p style={{ fontSize: '.78rem', color: C.mute, margin: '.7rem 0 0' }}>
          Aucun rapport n’est encore classé dans l’application ({sum?.sansRapport} sur{' '}
          {sum?.total}). C’est la pièce qui manque le plus : un contrôle sans son
          rapport ne se prouve pas.
        </p>
      </div>

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 940 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['N°', 'Installation / équipement', 'Type de contrôle', 'Organisme',
                'Dernier', 'Fréquence', 'Prochain', 'Statut', ''].map((h) => (
                <th key={h} style={{
                  textAlign: 'left', padding: '9px 10px', fontSize: '.75rem',
                  fontWeight: 700, color: C.mute, textTransform: 'uppercase',
                  letterSpacing: '.03em', borderBottom: `1px solid ${C.line}`,
                  whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const st = STATUS[r.status] || STATUS.A_DETERMINER;
              const cov = r.coveredBy;
              return (
                <Fragment key={r.id}>
                  <tr style={{
                    borderBottom: `1px solid ${C.line}`,
                    background: r.raisedByMensura ? '#fffbfb' : '#fff',
                  }}>
                    <td style={{ padding: '9px 10px', fontWeight: 700, color: C.mute,
                      fontVariantNumeric: 'tabular-nums' }}>
                      {r.seq === 100 ? '—' : r.seq}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: '.88rem', color: C.ink }}>
                      <div style={{ fontWeight: 600 }}>{r.installation}</div>
                      {r.raisedByMensura && (
                        <span style={{ fontSize: '.74rem', color: C.crit, fontWeight: 700 }}>
                          soulevé par Mensura le 01/09/2025
                        </span>
                      )}
                      {cov && (
                        <div style={{
                          fontSize: '.76rem', marginTop: 3,
                          color: cov.date ? (cov.conform === false ? C.crit : '#065F46') : C.mute,
                        }}>
                          {cov.date
                            ? `vérifié le ${fr(cov.date)} — ${cov.answer} — par ${cov.by || 'inconnu'}`
                            : 'lié à la tournée du vendredi, pas encore de trace'}
                        </div>
                      )}
                      {r.remarks && (
                        <div style={{ fontSize: '.78rem', color: C.mute, marginTop: 3,
                          maxWidth: '56ch' }}>{r.remarks}</div>
                      )}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: '.82rem', color: C.ink,
                      maxWidth: 200 }}>{r.controlType || '—'}</td>
                    <td style={{ padding: '9px 10px', fontSize: '.82rem', color: C.ink }}>
                      {r.organism || <span style={{ color: C.warn }}>à déterminer</span>}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                      {r.lastControlDate ? fr(r.lastControlDate)
                        : <span style={{ color: C.mute }}>{r.lastControlNote || '—'}</span>}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: '.82rem' }}>
                      {r.frequencyNote || (r.frequencyMonths ? `${r.frequencyMonths} mois` : '—')}
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: '.82rem', whiteSpace: 'nowrap',
                      color: r.overdue ? C.crit : C.ink, fontWeight: r.overdue ? 700 : 400 }}>
                      {fr(r.nextControlDate)}
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      <span title={st.help} style={{
                        display: 'inline-block', background: st.bg, color: st.fg,
                        borderRadius: 999, padding: '2px 9px', fontSize: '.76rem',
                        fontWeight: 700, whiteSpace: 'nowrap',
                      }}>{st.label}</span>
                    </td>
                    <td style={{ padding: '9px 10px' }}>
                      {canEdit && (
                        <button type="button" onClick={() => edit(r)} style={{
                          border: `1px solid ${C.line}`, background: '#fff',
                          borderRadius: 6, padding: '.25rem .6rem', fontSize: '.78rem',
                          cursor: 'pointer', color: C.brand, whiteSpace: 'nowrap',
                        }}>{open === r.id ? 'fermer' : 'compléter'}</button>
                      )}
                    </td>
                  </tr>

                  {open === r.id && (
                    <tr>
                      <td colSpan={9} style={{ padding: '1rem 1.1rem',
                        background: '#f8fafc', borderBottom: `1px solid ${C.line}` }}>
                        <div style={{ display: 'grid', gap: '.8rem',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
                          <div>
                            <label style={lbl}>Organisme</label>
                            <input style={inp} value={draft.organism}
                              onChange={(e) => setDraft((d) => ({ ...d, organism: e.target.value }))}
                              placeholder="Qui réalise le contrôle" />
                          </div>
                          <div>
                            <label style={lbl}>Type de contrôle</label>
                            <input style={inp} value={draft.controlType}
                              onChange={(e) => setDraft((d) => ({ ...d, controlType: e.target.value }))} />
                          </div>
                          <div>
                            <label style={lbl}>Dernier contrôle</label>
                            <input style={inp} type="date" value={draft.lastControlDate}
                              onChange={(e) => setDraft((d) => ({ ...d, lastControlDate: e.target.value }))} />
                          </div>
                          <div>
                            <label style={lbl}>Fréquence (mois)</label>
                            <input style={inp} type="number" min="1" max="240"
                              value={draft.frequencyMonths}
                              onChange={(e) => setDraft((d) => ({ ...d, frequencyMonths: e.target.value }))} />
                          </div>
                          <div>
                            <label style={lbl}>Fréquence — texte</label>
                            <input style={inp} value={draft.frequencyNote}
                              onChange={(e) => setDraft((d) => ({ ...d, frequencyNote: e.target.value }))}
                              placeholder="1 an (3 mois si location)" />
                          </div>
                          <div>
                            <label style={lbl}>Statut</label>
                            <select style={inp} value={draft.status}
                              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
                              {Object.entries(STATUS).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lbl}>Rapport classé — où il se trouve</label>
                            <input style={inp} value={draft.reportPath}
                              onChange={(e) => setDraft((d) => ({ ...d, reportPath: e.target.value }))}
                              placeholder="Z:\\…\\rapport_sprinkler_2026.pdf, ou « classeur PPT étagère 2 »" />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={lbl}>Remarques / action</label>
                            <textarea style={{ ...inp, minHeight: 62, resize: 'vertical' }}
                              value={draft.remarks}
                              onChange={(e) => setDraft((d) => ({ ...d, remarks: e.target.value }))} />
                          </div>
                        </div>
                        <p style={{ fontSize: '.78rem', color: C.mute, margin: '.7rem 0 .5rem' }}>
                          Si vous renseignez une date et une fréquence, la prochaine
                          échéance se calcule toute seule.
                        </p>
                        <button type="button" disabled={saving} onClick={() => save(r.id)}
                          style={{
                            padding: '.45rem 1rem', borderRadius: 8, border: 'none',
                            background: C.brand, color: '#fff', fontWeight: 600,
                            cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1,
                          }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!shown.length && (
              <tr><td colSpan={9} style={{ padding: '1.2rem', textAlign: 'center',
                color: C.mute }}>Aucune ligne pour ce filtre.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
